// src/pages/Partner/PartnerReports.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Activity,
  Award,
  BarChart3,
  CheckCircle2,
  Download,
  Eye,
  FileSpreadsheet,
  ListChecks,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import {
  Link,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import ExcelJS from "exceljs";

import { fetchPartnerCohorts } from "../../lib/partner/partnerCohorts";
import { fetchPartnerCohortProgress } from "../../lib/partner/partnerProgress";
import { fetchPartnerLearnerFacilitators } from "../../lib/partner/partnerFacilitators";

const REPORT_TYPES = [
  {
    id: "overview",
    label: "Cohort summary",
    icon: BarChart3,
  },
  {
    id: "roster",
    label: "Learner roster",
    icon: Users,
  },
  {
    id: "progress",
    label: "Learning progress",
    icon: Award,
  },
  {
    id: "tasks",
    label: "Task status",
    icon: ListChecks,
  },
];

function nullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}

function numberValue(value) {
  return nullableNumber(value) ?? 0;
}

function formatNumber(value) {
  const parsed = nullableNumber(value);

  if (parsed === null) {
    return "Not available";
  }

  return parsed.toLocaleString("en-ZA");
}

function formatPercentage(value) {
  const parsed = nullableNumber(value);

  if (parsed === null) {
    return "Not available";
  }

  return `${parsed.toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function positionLabel(row) {
  if (
    !row?.current_quest_id &&
    !row?.current_subquest_id &&
    !row?.current_level_key
  ) {
    return "Not started";
  }

  return [
    row.current_quest_id
      ? `Quest ${row.current_quest_id}`
      : null,

    row.current_subquest_id
      ? `Subquest ${row.current_subquest_id}`
      : null,

    row.current_level_key || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function activityDescription(row) {
  const days =
    nullableNumber(
      row?.days_since_activity
    );

  if (days === null) {
    return "No activity recorded";
  }

  if (days === 0) {
    return "Active today";
  }

  if (days === 1) {
    return "1 day since activity";
  }

  return `${days} days since activity`;
}

function membershipClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "completed":
      return "bg-blue-100 text-blue-800";

    case "removed":
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-amber-100 text-amber-800";
  }
}

function taskStatus(row) {
  const total =
    nullableNumber(row?.tasks_total);

  const completed =
    nullableNumber(
      row?.tasks_completed
    );

  const outstanding =
    nullableNumber(
      row?.tasks_outstanding
    );

  if (
    total === null ||
    total === 0
  ) {
    return "No unlocked tasks";
  }

  if (
    outstanding !== null &&
    outstanding > 0
  ) {
    return "Outstanding";
  }

  if (
    completed !== null &&
    completed >= total
  ) {
    return "Completed";
  }

  return "In progress";
}

function taskStatusClasses(status) {
  switch (status) {
    case "Completed":
      return "bg-green-100 text-green-800";

    case "Outstanding":
      return "bg-amber-100 text-amber-800";

    case "In progress":
      return "bg-blue-100 text-blue-800";

    default:
      return "bg-gray-200 text-gray-700";
  }
}

function sanitiseFilename(value) {
  return String(value || "cohort")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const EXCEL_THEME = {
  primary: "FFFF914D",
  primaryDark: "FFE9782F",
  lightOrange: "FFFFF4EC",
  cream: "FFFFF7ED",
  border: "FFFFE1CF",
  text: "FF333333",
  muted: "FF757575",
  white: "FFFFFFFF",
  greenFill: "FFE8F5E9",
  greenText: "FF2E7D32",
  blueFill: "FFE8F1FF",
  blueText: "FF1D4ED8",
  amberFill: "FFFFF3CD",
  amberText: "FF92400E",
  grayFill: "FFF1F1F1",
  grayText: "FF666666",
};

function thinBorder(
  color = EXCEL_THEME.border
) {
  return {
    top: {
      style: "thin",
      color: { argb: color },
    },
    bottom: {
      style: "thin",
      color: { argb: color },
    },
    left: {
      style: "thin",
      color: { argb: color },
    },
    right: {
      style: "thin",
      color: { argb: color },
    },
  };
}

function formatExportDateTime(value) {
  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value || "");
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function writeCellValue(
  cell,
  header,
  rawValue
) {
  if (
    rawValue === null ||
    rawValue === undefined ||
    rawValue === ""
  ) {
    cell.value = "";
    return;
  }

  if (
    typeof rawValue === "string" &&
    rawValue.trim().endsWith("%") &&
    /(progress|rate|percentage)/i.test(
      header
    )
  ) {
    const percentage =
      Number.parseFloat(rawValue);

    if (Number.isFinite(percentage)) {
      cell.value =
        percentage / 100;

      cell.numFmt = "0.0%";

      return;
    }
  }

  cell.value = rawValue;

  if (typeof rawValue === "number") {
    cell.numFmt =
      Number.isInteger(rawValue)
        ? "#,##0"
        : "#,##0.0";
  }
}

function applyStatusStyle(
  cell,
  value
) {
  const status =
    String(value || "")
      .trim()
      .toLowerCase();

  if (
    ["active", "completed"].includes(
      status
    )
  ) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb:
          status === "active"
            ? EXCEL_THEME.greenFill
            : EXCEL_THEME.blueFill,
      },
    };

    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: {
        argb:
          status === "active"
            ? EXCEL_THEME.greenText
            : EXCEL_THEME.blueText,
      },
    };

    return;
  }

  if (
    status.includes("outstanding") ||
    status.includes("in progress")
  ) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb:
          EXCEL_THEME.amberFill,
      },
    };

    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: {
        argb:
          EXCEL_THEME.amberText,
      },
    };

    return;
  }

  if (
    status.includes("removed") ||
    status.includes("no unlocked") ||
    status.includes("not available")
  ) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb:
          EXCEL_THEME.grayFill,
      },
    };

    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: {
        argb:
          EXCEL_THEME.grayText,
      },
    };
  }
}

function addWorkbookHeading({
  worksheet,
  title,
  columnCount,
  organisationName,
  cohortName,
  generatedAt,
  membershipFilter,
  searchFilter,
}) {
  const lastColumn =
    Math.max(
      columnCount,
      2
    );

  worksheet.mergeCells(
    1,
    1,
    1,
    lastColumn
  );

  const titleCell =
    worksheet.getCell(1, 1);

  titleCell.value = title;

  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: {
      argb:
        EXCEL_THEME.primary,
    },
  };

  titleCell.font = {
    name: "Calibri",
    size: 18,
    bold: true,
    color: {
      argb:
        EXCEL_THEME.white,
    },
  };

  titleCell.alignment = {
    vertical: "middle",
    horizontal: "left",
  };

  worksheet.getRow(1).height =
    34;

  const metadata = [
    [
      "Organisation",
      organisationName ||
        "Partner organisation",
    ],
    [
      "Cohort",
      cohortName ||
        "Not selected",
    ],
    [
      "Generated",
      formatExportDateTime(
        generatedAt
      ),
    ],
    [
      "Filters",
      `Membership: ${
        membershipFilter ||
        "All learners"
      } · Search: ${
        searchFilter ||
        "None"
      }`,
    ],
  ];

  metadata.forEach(
    ([label, value], index) => {
      const rowNumber =
        index + 2;

      const labelCell =
        worksheet.getCell(
          rowNumber,
          1
        );

      const valueCell =
        worksheet.getCell(
          rowNumber,
          2
        );

      labelCell.value =
        label;

      labelCell.font = {
        name: "Calibri",
        size: 10,
        bold: true,
        color: {
          argb:
            EXCEL_THEME.primaryDark,
        },
      };

      labelCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            EXCEL_THEME.lightOrange,
        },
      };

      labelCell.alignment = {
        vertical: "middle",
      };

      if (lastColumn > 2) {
        worksheet.mergeCells(
          rowNumber,
          2,
          rowNumber,
          lastColumn
        );
      }

      valueCell.value =
        value;

      valueCell.font = {
        name: "Calibri",
        size: 10,
        color: {
          argb:
            EXCEL_THEME.text,
        },
      };

      valueCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            EXCEL_THEME.cream,
        },
      };

      valueCell.alignment = {
        vertical: "middle",
        wrapText: true,
      };

      for (
        let column = 1;
        column <= lastColumn;
        column += 1
      ) {
        worksheet.getCell(
          rowNumber,
          column
        ).border =
          thinBorder();
      }

      worksheet.getRow(
        rowNumber
      ).height = 22;
    }
  );

  worksheet.getRow(6).height =
    9;
}

function styleHeaderRow(row) {
  row.height = 28;

  row.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb:
          EXCEL_THEME.primaryDark,
      },
    };

    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: {
        argb:
          EXCEL_THEME.white,
      },
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };

    cell.border =
      thinBorder(
        EXCEL_THEME.primary
      );
  });
}

function styleDataRow(
  row,
  rowIndex,
  headers
) {
  row.height = 23;

  row.eachCell(
    {
      includeEmpty: true,
    },
    (
      cell,
      columnNumber
    ) => {
      const header =
        headers[
          columnNumber - 1
        ] || "";

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            rowIndex % 2 === 0
              ? EXCEL_THEME.cream
              : EXCEL_THEME.white,
        },
      };

      cell.font = {
        name: "Calibri",
        size: 11,
        color: {
          argb:
            EXCEL_THEME.text,
        },
        bold:
          columnNumber === 1,
      };

      cell.alignment = {
        vertical: "middle",
        horizontal:
          typeof cell.value ===
          "number"
            ? "right"
            : "left",
        wrapText: true,
      };

      cell.border = {
        bottom: {
          style: "hair",
          color: {
            argb:
              EXCEL_THEME.border,
          },
        },
      };

      if (
        /status/i.test(
          header
        )
      ) {
        applyStatusStyle(
          cell,
          cell.value
        );
      }
    }
  );
}

function calculateColumnWidth(
  header,
  rows
) {
  const contentWidth =
    rows.reduce(
      (maximum, row) => {
        const value =
          row?.[header];

        const length =
          value === null ||
          value === undefined
            ? 0
            : String(
                value
              ).length;

        return Math.max(
          maximum,
          length
        );
      },
      String(header).length
    );

  return Math.min(
    Math.max(
      contentWidth + 3,
      13
    ),
    42
  );
}

function renderSummaryWorksheet({
  worksheet,
  rows,
  organisationName,
  cohortName,
  generatedAt,
  membershipFilter,
  searchFilter,
}) {
  addWorkbookHeading({
    worksheet,
    title:
      "Gwin Partner Report · Cohort Summary",
    columnCount: 2,
    organisationName,
    cohortName,
    generatedAt,
    membershipFilter,
    searchFilter,
  });

  const headerRow =
    worksheet.getRow(7);

  headerRow.values = [
    "Metric",
    "Value",
  ];

  styleHeaderRow(
    headerRow
  );

  const summary =
    rows?.[0] || {};

  const entries =
    Object.entries(
      summary
    ).filter(
      ([key]) =>
        ![
          "Organisation",
          "Cohort",
        ].includes(key)
    );

  entries.forEach(
    (
      [metric, value],
      index
    ) => {
      const row =
        worksheet.getRow(
          index + 8
        );

      row.getCell(1).value =
        metric;

      writeCellValue(
        row.getCell(2),
        metric,
        value
      );

      styleDataRow(
        row,
        index,
        [
          "Metric",
          "Value",
        ]
      );

      row.getCell(1).font = {
        name: "Calibri",
        size: 11,
        bold: true,
        color: {
          argb:
            EXCEL_THEME.text,
        },
      };

      row.getCell(2).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            index % 2 === 0
              ? EXCEL_THEME.lightOrange
              : EXCEL_THEME.cream,
        },
      };

      row.getCell(2).font = {
        name: "Calibri",
        size: 11,
        bold: true,
        color: {
          argb:
            EXCEL_THEME.primaryDark,
        },
      };
    }
  );

  const noteRowNumber =
    entries.length + 9;

  worksheet.mergeCells(
    noteRowNumber,
    1,
    noteRowNumber,
    2
  );

  const noteCell =
    worksheet.getCell(
      noteRowNumber,
      1
    );

  noteCell.value =
    "This workbook uses existing learning, activity and practical-task records. Financial growth, behaviour and risk reporting will be added after those data-collection features are implemented.";

  noteCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: {
      argb:
        EXCEL_THEME.lightOrange,
    },
  };

  noteCell.font = {
    name: "Calibri",
    size: 10,
    italic: true,
    color: {
      argb:
        EXCEL_THEME.muted,
    },
  };

  noteCell.alignment = {
    vertical: "middle",
    wrapText: true,
  };

  noteCell.border =
    thinBorder();

  worksheet.getRow(
    noteRowNumber
  ).height = 44;

  worksheet.getColumn(
    1
  ).width = 38;

  worksheet.getColumn(
    2
  ).width = 28;

  worksheet.views = [
    {
      state: "frozen",
      ySplit: 7,
      activeCell: "A8",
      showGridLines: false,
    },
  ];
}

function renderTableWorksheet({
  worksheet,
  title,
  rows,
  organisationName,
  cohortName,
  generatedAt,
  membershipFilter,
  searchFilter,
}) {
  const safeRows =
    Array.isArray(rows) &&
    rows.length
      ? rows
      : [
          {
            Message:
              "No data available",
          },
        ];

  const headers =
    Object.keys(
      safeRows[0]
    );

  addWorkbookHeading({
    worksheet,
    title:
      `Gwin Partner Report · ${title}`,
    columnCount:
      headers.length,
    organisationName,
    cohortName,
    generatedAt,
    membershipFilter,
    searchFilter,
  });

  const headerRow =
    worksheet.getRow(7);

  headers.forEach(
    (header, index) => {
      headerRow.getCell(
        index + 1
      ).value = header;
    }
  );

  styleHeaderRow(
    headerRow
  );

  safeRows.forEach(
    (
      sourceRow,
      rowIndex
    ) => {
      const row =
        worksheet.getRow(
          rowIndex + 8
        );

      headers.forEach(
        (
          header,
          columnIndex
        ) => {
          writeCellValue(
            row.getCell(
              columnIndex + 1
            ),
            header,
            sourceRow?.[
              header
            ]
          );
        }
      );

      styleDataRow(
        row,
        rowIndex,
        headers
      );
    }
  );

  headers.forEach(
    (header, index) => {
      worksheet.getColumn(
        index + 1
      ).width =
        calculateColumnWidth(
          header,
          safeRows
        );
    }
  );

  worksheet.autoFilter = {
    from: {
      row: 7,
      column: 1,
    },
    to: {
      row: 7,
      column:
        headers.length,
    },
  };

  worksheet.views = [
    {
      state: "frozen",
      ySplit: 7,
      activeCell: "A8",
      showGridLines: false,
    },
  ];
}

async function downloadExcelWorkbook({
  sheets,
  filename,
  organisationName,
  cohortName,
  membershipFilter,
  searchFilter,
}) {
  if (
    !Array.isArray(sheets) ||
    sheets.length === 0
  ) {
    throw new Error(
      "There is no report data to export."
    );
  }

  const workbook =
    new ExcelJS.Workbook();

  const generatedAt =
    new Date();

  workbook.creator =
    "Gwin";

  workbook.lastModifiedBy =
    "Gwin Partner Portal";

  workbook.created =
    generatedAt;

  workbook.modified =
    generatedAt;

  workbook.subject =
    "Gwin Partner Portal report";

  workbook.title =
    "Gwin Partner Report";

  workbook.company =
    "Gwin";

  sheets.forEach(
    (
      {
        name,
        rows,
      },
      index
    ) => {
      const worksheet =
        workbook.addWorksheet(
          name,
          {
            properties: {
              tabColor: {
                argb:
                  index === 0
                    ? EXCEL_THEME.primaryDark
                    : EXCEL_THEME.primary,
              },
            },

            pageSetup: {
              orientation:
                "landscape",
              fitToPage: true,
              fitToWidth: 1,
              fitToHeight: 0,
              paperSize: 9,

              margins: {
                left: 0.3,
                right: 0.3,
                top: 0.5,
                bottom: 0.5,
                header: 0.2,
                footer: 0.2,
              },
            },
          }
        );

      const commonSettings = {
        worksheet,
        rows,
        organisationName,
        cohortName,
        generatedAt,
        membershipFilter,
        searchFilter,
      };

      if (
        name ===
        "Cohort Summary"
      ) {
        renderSummaryWorksheet(
          commonSettings
        );
      } else {
        renderTableWorksheet({
          ...commonSettings,
          title: name,
        });
      }
    }
  );

  const buffer =
    await workbook.xlsx.writeBuffer();

  const blob =
    new Blob(
      [buffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );

  const url =
    URL.createObjectURL(
      blob
    );

  const link =
    document.createElement(
      "a"
    );

  link.href = url;
  link.download =
    filename;

  document.body.appendChild(
    link
  );

  link.click();
  link.remove();

  URL.revokeObjectURL(
    url
  );
}

export default function PartnerReports() {
  const {
    access,
  } = useOutletContext();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const requestedCohortId =
    searchParams.get(
      "cohort"
    ) || "";

  const autoExportRequested =
    searchParams.get(
      "export"
    ) === "1";

  const autoExportHandledRef =
    useRef(false);

  const [
    cohorts,
    setCohorts,
  ] = useState([]);

  const [
    selectedCohortId,
    setSelectedCohortId,
  ] = useState("");

  const [
    learners,
    setLearners,
  ] = useState([]);

  const [
    reportType,
    setReportType,
  ] = useState("overview");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    membershipFilter,
    setMembershipFilter,
  ] = useState("all");

  const [
    loadingCohorts,
    setLoadingCohorts,
  ] = useState(true);

  const [
    loadingReport,
    setLoadingReport,
  ] = useState(false);

  const [
    exporting,
    setExporting,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const canViewFacilitatorTags =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(
      access?.partner_role
    );

  const selectedCohort =
    useMemo(
      () =>
        cohorts.find(
          (cohort) =>
            cohort.id ===
            selectedCohortId
        ) || null,
      [
        cohorts,
        selectedCohortId,
      ]
    );

  const loadCohorts =
    useCallback(
      async () => {
        try {
          setLoadingCohorts(
            true
          );

          setError("");

          const cohortRows =
            await fetchPartnerCohorts();

          setCohorts(
            Array.isArray(
              cohortRows
            )
              ? cohortRows
              : []
          );

          setSelectedCohortId(
            (
              currentId
            ) => {
              const safeRows =
                Array.isArray(
                  cohortRows
                )
                  ? cohortRows
                  : [];

              const requestedCohortExists =
                requestedCohortId &&
                safeRows.some(
                  (cohort) =>
                    String(
                      cohort.id
                    ) ===
                    String(
                      requestedCohortId
                    )
                );

              if (
                requestedCohortExists
              ) {
                return requestedCohortId;
              }

              const currentStillExists =
                safeRows.some(
                  (cohort) =>
                    cohort.id ===
                    currentId
                );

              if (
                currentStillExists
              ) {
                return currentId;
              }

              return (
                safeRows[0]?.id ||
                ""
              );
            }
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner report cohort loading failed:",
            loadError
          );

          setCohorts([]);

          setError(
            loadError?.message ||
              "The available cohorts could not be loaded."
          );
        } finally {
          setLoadingCohorts(
            false
          );
        }
      },
      [
        requestedCohortId,
      ]
    );

  const loadReport =
    useCallback(
      async () => {
        if (
          !selectedCohortId
        ) {
          setLearners([]);
          return;
        }

        try {
          setLoadingReport(
            true
          );

          setError("");
          setMessage("");

          const [
            progressRows,
            facilitatorRows,
          ] =
            await Promise.all(
              [
                fetchPartnerCohortProgress(
                  selectedCohortId
                ),

                canViewFacilitatorTags
                  ? fetchPartnerLearnerFacilitators(
                      selectedCohortId
                    )
                  : Promise.resolve(
                      []
                    ),
              ]
            );

          const safeProgressRows =
            Array.isArray(
              progressRows
            )
              ? progressRows
              : [];

          const safeFacilitatorRows =
            Array.isArray(
              facilitatorRows
            )
              ? facilitatorRows
              : [];

          const facilitatorByLearnerId =
            new Map(
              safeFacilitatorRows.map(
                (
                  assignment
                ) => [
                  assignment.cohort_learner_id,
                  assignment,
                ]
              )
            );

          setLearners(
            safeProgressRows.map(
              (learner) => ({
                ...learner,

                facilitator_assignment:
                  canViewFacilitatorTags
                    ? facilitatorByLearnerId.get(
                        learner.cohort_learner_id
                      ) ||
                      null
                    : null,
              })
            )
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner report loading failed:",
            loadError
          );

          setLearners(
            []
          );

          setError(
            loadError?.message ||
              "The selected report could not be loaded."
          );
        } finally {
          setLoadingReport(
            false
          );
        }
      },
      [
        selectedCohortId,
        canViewFacilitatorTags,
      ]
    );

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredLearners =
    useMemo(() => {
      const cleanSearch =
        search
          .trim()
          .toLowerCase();

      return learners.filter(
        (learner) => {
          const matchesStatus =
            membershipFilter ===
              "all" ||
            learner.membership_status ===
              membershipFilter;

          if (
            !matchesStatus
          ) {
            return false;
          }

          if (
            !cleanSearch
          ) {
            return true;
          }

          const facilitator =
  canViewFacilitatorTags
    ? learner.facilitator_assignment
    : null;


return [
  learner.full_name,
  learner.email,
  learner.membership_status,
  learner.current_quest_id,
  learner.current_subquest_id,
  learner.current_level_key,
  learner.last_completed_level_title,

  canViewFacilitatorTags
    ? facilitator?.facilitator_full_name
    : "",

  canViewFacilitatorTags
    ? facilitator?.facilitator_email
    : "",
].some((value) =>
  String(value || "")
    .toLowerCase()
    .includes(cleanSearch)
);
        }
      );
    }, [
      learners,
      membershipFilter,
      search,
      canViewFacilitatorTags,
    ]);

  const summary =
    useMemo(() => {
      const progressValues =
        filteredLearners
          .map(
            (
              learner
            ) =>
              nullableNumber(
                learner.overall_progress_percent
              )
          )
          .filter(
            (value) =>
              value !== null
          );

      const totalProgress =
        progressValues.reduce(
          (
            total,
            value
          ) =>
            total +
            value,
          0
        );

      return filteredLearners.reduce(
        (
          result,
          learner
        ) => {
          result.levelsCompleted +=
            numberValue(
              learner.levels_completed
            );

          result.tasksCompleted +=
            numberValue(
              learner.tasks_completed
            );

          result.tasksOutstanding +=
            numberValue(
              learner.tasks_outstanding
            );

          if (
            learner.last_activity_at
          ) {
            result.activityRecorded +=
              1;
          }

          if (
            learner.membership_status ===
            "active"
          ) {
            result.activeMemberships +=
              1;
          }

          if (
            learner.membership_status ===
            "completed"
          ) {
            result.completedMemberships +=
              1;
          }

          return result;
        },
        {
          levelsCompleted: 0,
          tasksCompleted: 0,
          tasksOutstanding: 0,
          activityRecorded: 0,
          activeMemberships: 0,
          completedMemberships: 0,

          averageProgress:
            progressValues.length
              ? totalProgress /
                progressValues.length
              : null,

          progressCoverage:
            progressValues.length,
        }
      );
    }, [
      filteredLearners,
    ]);

  const exportWorkbookSheets =
    useMemo(() => {
      const summaryRows = [
        {
          Organisation:
            access?.partner_name ||
            "",

          Cohort:
            selectedCohort?.name ||
            "",

          "Learners included":
            filteredLearners.length,

          "Active memberships":
            summary.activeMemberships,

          "Completed memberships":
            summary.completedMemberships,

          "Levels completed":
            summary.levelsCompleted,

          "Average progress":
            summary.averageProgress ===
            null
              ? ""
              : `${summary.averageProgress.toFixed(
                  1
                )}%`,

          "Progress data coverage":
            `${summary.progressCoverage} of ${filteredLearners.length}`,

          "Tasks completed":
            summary.tasksCompleted,

          "Tasks outstanding":
            summary.tasksOutstanding,

          "Learners with activity recorded":
            summary.activityRecorded,
        },
      ];

      const rosterRows =
        filteredLearners.map(
          (learner) => ({
            "Learner name":
              learner.full_name ||
              "",

            Email:
              learner.email ||
              "",

            ...(canViewFacilitatorTags
              ? {
                  Facilitator:
                    learner
                      .facilitator_assignment
                      ?.facilitator_partner_member_id
                      ? learner
                          .facilitator_assignment
                          .facilitator_full_name ||
                        "Facilitator"
                      : "Unassigned",

                  "Facilitator email":
                    learner
                      .facilitator_assignment
                      ?.facilitator_email ||
                    "",
                }
              : {}),

            "Membership status":
              learner.membership_status ||
              "",

            "Current position":
              positionLabel(
                learner
              ),

            "Latest completed level":
              learner.last_completed_level_title ||
              "",

            "Latest level completed at":
              learner.last_completed_at
                ? formatDateTime(
                    learner.last_completed_at
                  )
                : "",

            "Last app activity":
              learner.last_activity_at
                ? formatDateTime(
                    learner.last_activity_at
                  )
                : "",

            "Last successful login":
              learner.last_login_at
                ? formatDateTime(
                    learner.last_login_at
                  )
                : "",
          })
        );

      const progressRows =
        filteredLearners.map(
          (learner) => ({
            "Learner name":
              learner.full_name ||
              "",

            Email:
              learner.email ||
              "",

            ...(canViewFacilitatorTags
              ? {
                  Facilitator:
                    learner
                      .facilitator_assignment
                      ?.facilitator_partner_member_id
                      ? learner
                          .facilitator_assignment
                          .facilitator_full_name ||
                        "Facilitator"
                      : "Unassigned",

                  "Facilitator email":
                    learner
                      .facilitator_assignment
                      ?.facilitator_email ||
                    "",
                }
              : {}),

            "Membership status":
              learner.membership_status ||
              "",

            "Current position":
              positionLabel(
                learner
              ),

            "Levels completed":
              nullableNumber(
                learner.levels_completed
              ) ?? "",

            "Total levels":
              nullableNumber(
                learner.total_levels
              ) ?? "",

            "Overall progress":
              nullableNumber(
                learner.overall_progress_percent
              ) === null
                ? ""
                : `${numberValue(
                    learner.overall_progress_percent
                  ).toFixed(
                    1
                  )}%`,

            "Total XP":
              nullableNumber(
                learner.total_xp
              ) ?? "",

            "Latest completed level":
              learner.last_completed_level_title ||
              "",

            "Latest completion date":
              learner.last_completed_at
                ? formatDateTime(
                    learner.last_completed_at
                  )
                : "",
          })
        );

      const taskRows =
        filteredLearners.map(
          (learner) => {
            const total =
              nullableNumber(
                learner.tasks_total
              );

            const completed =
              nullableNumber(
                learner.tasks_completed
              );

            const completionRate =
              total !== null &&
              total > 0 &&
              completed !== null
                ? `${(
                    (completed /
                      total) *
                    100
                  ).toFixed(
                    1
                  )}%`
                : "";

            return {
              "Learner name":
                learner.full_name ||
                "",

              Email:
                learner.email ||
                "",

              ...(canViewFacilitatorTags
                ? {
                    Facilitator:
                      learner
                        .facilitator_assignment
                        ?.facilitator_partner_member_id
                        ? learner
                            .facilitator_assignment
                            .facilitator_full_name ||
                          "Facilitator"
                        : "Unassigned",

                    "Facilitator email":
                      learner
                        .facilitator_assignment
                        ?.facilitator_email ||
                      "",
                  }
                : {}),

              "Membership status":
                learner.membership_status ||
                "",

              "Task status":
                taskStatus(
                  learner
                ),

              "Tasks completed":
                completed ??
                "",

              "Total unlocked tasks":
                total ??
                "",

              "Tasks outstanding":
                nullableNumber(
                  learner.tasks_outstanding
                ) ?? "",

              "Task completion rate":
                completionRate,
            };
          }
        );

      return [
        {
          name:
            "Cohort Summary",
          rows:
            summaryRows,
        },
        {
          name:
            "Learner Roster",
          rows:
            rosterRows,
        },
        {
          name:
            "Learning Progress",
          rows:
            progressRows,
        },
        {
          name:
            "Task Status",
          rows:
            taskRows,
        },
      ];
    }, [
      access?.partner_name,
      filteredLearners,
      selectedCohort?.name,
      summary,
      canViewFacilitatorTags,
    ]);

  async function handleRefresh() {
    setMessage("");

    await loadCohorts();

    if (
      selectedCohortId
    ) {
      await loadReport();
    }
  }

  const handleExport =
    useCallback(
      async () => {
    try {
      setExporting(
        true
      );

      setError("");
      setMessage("");

      if (
        filteredLearners.length ===
        0
      ) {
        throw new Error(
          "There is no report data to export."
        );
      }

      const today =
        new Date()
          .toISOString()
          .slice(0, 10);

      const filename = [
        sanitiseFilename(
          selectedCohort?.name
        ),
        "partner-report",
        today,
      ]
        .filter(Boolean)
        .join("-");

      await downloadExcelWorkbook({
        sheets:
          exportWorkbookSheets,

        filename:
          `${filename || "partner-report"}.xlsx`,

        organisationName:
          access?.partner_name ||
          "Partner organisation",

        cohortName:
          selectedCohort?.name ||
          "Not selected",

        membershipFilter:
          membershipFilter ===
          "all"
            ? "All learners"
            : membershipFilter,

        searchFilter:
          search.trim() ||
          "None",
      });

      setMessage(
        "Excel workbook exported successfully."
      );
    } catch (
      exportError
    ) {
      console.error(
        "Partner report export failed:",
        exportError
      );

      setError(
        exportError?.message ||
          "The report could not be exported."
      );
    } finally {
      setExporting(
        false
      );
    }
      },
      [
        access?.partner_name,
        exportWorkbookSheets,
        filteredLearners.length,
        membershipFilter,
        search,
        selectedCohort?.name,
      ]
    );


  useEffect(() => {
    if (
      !autoExportRequested
    ) {
      autoExportHandledRef.current =
        false;

      return;
    }

    if (
      autoExportHandledRef.current ||
      loadingCohorts ||
      loadingReport ||
      exporting ||
      !selectedCohortId
    ) {
      return;
    }

    if (
      requestedCohortId &&
      String(
        selectedCohortId
      ) !==
        String(
          requestedCohortId
        )
    ) {
      return;
    }

    autoExportHandledRef.current =
      true;

    handleExport()
      .finally(() => {
        const nextParams =
          new URLSearchParams(
            searchParams
          );

        nextParams.delete(
          "export"
        );

        setSearchParams(
          nextParams,
          {
            replace: true,
          }
        );
      });
  }, [
    autoExportRequested,
    exporting,
    handleExport,
    loadingCohorts,
    loadingReport,
    requestedCohortId,
    searchParams,
    selectedCohortId,
    setSearchParams,
  ]);


  if (
    loadingCohorts
  ) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-[28px] border border-[#EFEFEF] bg-white">
        <div className="text-center">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

          <p className="mt-3 text-sm font-bold text-[#757575]">
            Loading reports…
          </p>
        </div>
      </div>
    );
  }

  if (
    error &&
    cohorts.length === 0
  ) {
    return (
      <section className="rounded-[28px] border border-red-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-black">
          Reports unavailable
        </h1>

        <p className="mt-3 text-sm text-red-700">
          {error}
        </p>

        <button
          type="button"
          onClick={
            loadCohorts
          }
          className="mt-6 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"
        >
          <RefreshCw className="mr-2 h-4 w-4" />

          Try again
        </button>
      </section>
    );
  }

  if (
    cohorts.length === 0
  ) {
    return (
      <section className="rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <FileSpreadsheet className="h-7 w-7" />
        </div>

        <h1 className="mt-5 text-2xl font-black">
          No report data available
        </h1>

        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#757575]">
          Reports will become available after an authorised cohort has been created.
        </p>

        <Link
          to="/partner/cohorts"
          className="mt-6 inline-flex rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"
        >
          View cohorts
        </Link>
      </section>
    );
  }

  return (
    <>
      <header className="rounded-[28px] border border-[#FFE1CF] bg-white p-6 shadow-sm lg:p-8">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-start">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Reports
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Reports 
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-[#757575] md:text-base">
               Review programme progress and export authorised learner
               information for the cohorts available to your account.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={
                handleRefresh
              }
              disabled={
                loadingReport ||
                exporting
              }
              className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] px-5 py-3 text-sm font-black transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={[
                  "mr-2 h-4 w-4",
                  loadingReport
                    ? "animate-spin"
                    : "",
                ].join(
                  " "
                )}
              />

              Refresh
            </button>

            <button
              type="button"
              onClick={
                handleExport
              }
              disabled={
                loadingReport ||
                exporting ||
                filteredLearners.length ===
                  0
              }
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}

              {exporting
                ? "Exporting…"
                : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="mt-7 grid gap-4 md:grid-cols-[minmax(240px,1fr)_minmax(240px,1fr)]">
          <div>
            <label
              htmlFor="report-cohort"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Cohort
            </label>

            <select
              id="report-cohort"
              value={
                selectedCohortId
              }
              onChange={(
                event
              ) => {
                setSelectedCohortId(
                  event.target.value
                );

                setSearch("");

                setMembershipFilter(
                  "all"
                );

                setMessage("");
              }}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-bold outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              {cohorts.map(
                (cohort) => (
                  <option
                    key={
                      cohort.id
                    }
                    value={
                      cohort.id
                    }
                  >
                    {cohort.name}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <p className="block text-xs font-black uppercase tracking-wider text-[#757575]">
              Organisation
            </p>

            <div className="mt-2 rounded-2xl border border-[#EFEFEF] bg-[#FFF9F5] px-4 py-3 text-sm font-black">
              {access?.partner_name ||
                "Partner organisation"}
            </div>
          </div>
        </div>
      </header>

      {message && (
        <div className="mt-5 flex items-start rounded-[22px] border border-green-200 bg-green-50 p-4 text-sm font-semibold text-green-800">
          <CheckCircle2 className="mr-3 mt-0.5 h-5 w-5 flex-none" />

          {message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-4 rounded-[22px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              loadReport
            }
            className="inline-flex flex-none items-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>
        </div>
      )}

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-sm">
        <div className="flex gap-2 overflow-x-auto">
          {REPORT_TYPES.map(
            (report) => {
              const Icon =
                report.icon;

              const active =
                reportType ===
                report.id;

              return (
                <button
                  key={
                    report.id
                  }
                  type="button"
                  onClick={() => {
                    setReportType(
                      report.id
                    );

                    setMessage("");
                  }}
                  className={[
                    "inline-flex flex-none items-center rounded-full px-4 py-2.5 text-sm font-black transition",
                    active
                      ? "bg-[#FF914D] text-white"
                      : "bg-[#FFF4EC] text-[#75523B] hover:bg-[#FFE1CF]",
                  ].join(
                    " "
                  )}
                >
                  <Icon className="mr-2 h-4 w-4" />

                  {report.label}
                </button>
              );
            }
          )}
        </div>
      </section>

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[minmax(220px,1fr)_220px]">
          <div>
            <label
              htmlFor="report-search"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Search learners
            </label>

            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="report-search"
                value={
                  search
                }
                onChange={(
                  event
                ) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder={
                  canViewFacilitatorTags
                    ? "Search name, email, quest, level, or facilitator"
                    : "Search name, email, quest or level"
                }
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="report-status"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Membership status
            </label>

            <select
              id="report-status"
              value={
                membershipFilter
              }
              onChange={(
                event
              ) =>
                setMembershipFilter(
                  event.target.value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-bold outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="all">
                All learners
              </option>

              <option value="active">
                Active
              </option>

              <option value="completed">
                Completed
              </option>
            </select>
          </div>
        </div>

        <p className="mt-4 text-xs font-semibold text-[#757575]">
          Showing{" "}
          <span className="font-black text-[#333333]">
            {filteredLearners.length}
          </span>{" "}
          of{" "}
          <span className="font-black text-[#333333]">
            {learners.length}
          </span>{" "}
          learner records.
        </p>
      </section>

      {loadingReport ? (
        <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-10 text-center shadow-sm">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

          <p className="mt-3 text-sm font-bold text-[#757575]">
            Loading report data…
          </p>
        </div>
      ) : filteredLearners.length ===
        0 ? (
        <section className="mt-6 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <FileSpreadsheet className="h-7 w-7" />
          </div>

          <h2 className="mt-5 text-xl font-black">
            No report records found
          </h2>

          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
            No learners match the selected cohort and current filters.
          </p>
        </section>
      ) : (
        <>
          {reportType ===
            "overview" && (
            <OverviewReport
              summary={
                summary
              }
              learnerCount={
                filteredLearners.length
              }
            />
          )}

          {reportType ===
            "roster" && (
            <RosterReport
              learners={
                filteredLearners
              }
              showFacilitator={
                canViewFacilitatorTags
              }
            />
          )}

          {reportType ===
            "progress" && (
            <ProgressReport
              learners={
                filteredLearners
              }
              showFacilitator={
                canViewFacilitatorTags
              }
            />
          )}

          {reportType ===
            "tasks" && (
            <TaskReport
              learners={
                filteredLearners
              }
              showFacilitator={
                canViewFacilitatorTags
              }
            />
          )}
        </>
      )}
    </>
  );
}

function OverviewReport({
  summary,
  learnerCount,
}) {
  return (
    <section className="mt-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Learners included"
          value={
            learnerCount
          }
          description="Matching current filters"
          icon={Users}
        />

        <SummaryCard
          label="Levels completed"
          value={
            summary.levelsCompleted
          }
          description="Across included learners"
          icon={Award}
        />

        <SummaryCard
          label="Average progress"
          value={
            summary.averageProgress ===
            null
              ? "Not available"
              : `${summary.averageProgress.toFixed(
                  1
                )}%`
          }
          description={`${summary.progressCoverage} of ${learnerCount} records included`}
          icon={
            BarChart3
          }
        />

        <SummaryCard
          label="Activity recorded"
          value={
            summary.activityRecorded
          }
          description="Learners with recorded activity"
          icon={Activity}
        />

        <SummaryCard
          label="Active memberships"
          value={
            summary.activeMemberships
          }
          description="Currently active"
          icon={Users}
        />

        <SummaryCard
          label="Completed memberships"
          value={
            summary.completedMemberships
          }
          description="Marked as completed"
          icon={
            CheckCircle2
          }
        />

        <SummaryCard
          label="Tasks completed"
          value={
            summary.tasksCompleted
          }
          description="Unlocked tasks completed"
          icon={
            ListChecks
          }
        />

        <SummaryCard
          label="Tasks outstanding"
          value={
            summary.tasksOutstanding
          }
          description="Unlocked but incomplete"
          icon={
            ListChecks
          }
        />
      </div>

      <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 text-sm leading-6 text-[#757575] shadow-sm">
        This summary uses existing learning and task records only. Financial growth, behaviour and risk reporting will remain unavailable until their data-collection features and rules have been implemented.
      </div>
    </section>
  );
}

function RosterReport({
  learners,
  showFacilitator,
}) {
  return (
    <ReportTable
      minimumWidth={
        showFacilitator
          ? "1420px"
          : "1180px"
      }
    >
      <thead className="bg-[#FFF9F5]">
        <tr>
          <TableHeading>
            Learner
          </TableHeading>

          {showFacilitator && (
            <TableHeading>
              Facilitator
            </TableHeading>
          )}

          <TableHeading>
            Status
          </TableHeading>

          <TableHeading>
            Current position
          </TableHeading>

          <TableHeading>
            Latest completed level
          </TableHeading>

          <TableHeading>
            Activity
          </TableHeading>

          <TableHeading align="right">
            Action
          </TableHeading>
        </tr>
      </thead>

      <tbody className="divide-y divide-[#EFEFEF]">
        {learners.map(
          (learner) => (
            <tr
              key={
                learner.cohort_learner_id
              }
              className="align-top transition hover:bg-[#FFFDFB]"
            >
              <td className="px-5 py-4">
                <p className="font-black">
                  {learner.full_name ||
                    "Learner"}
                </p>

                <p className="mt-1 text-xs font-semibold text-[#757575]">
                  {learner.email}
                </p>
              </td>

              {showFacilitator && (
                <td className="px-5 py-4">
                  <FacilitatorDetails
                    assignment={
                      learner.facilitator_assignment
                    }
                  />
                </td>
              )}

              <td className="px-5 py-4">
                <MembershipBadge
                  status={
                    learner.membership_status
                  }
                />
              </td>

              <td className="px-5 py-4 text-sm font-black">
                {positionLabel(
                  learner
                )}
              </td>

              <td className="px-5 py-4">
                <p className="max-w-[240px] text-sm font-black">
                  {learner.last_completed_level_title ||
                    "Not started"}
                </p>

                <p className="mt-1 text-xs text-[#757575]">
                  {formatDateTime(
                    learner.last_completed_at
                  )}
                </p>
              </td>

              <td className="px-5 py-4">
                <p className="text-sm font-black">
                  {activityDescription(
                    learner
                  )}
                </p>

                <p className="mt-1 text-xs text-[#757575]">
                  {formatDateTime(
                    learner.last_activity_at
                  )}
                </p>
              </td>

              <td className="px-5 py-4 text-right">
                <LearnerDetailsLink
                  learner={
                    learner
                  }
                />
              </td>
            </tr>
          )
        )}
      </tbody>
    </ReportTable>
  );
}

function ProgressReport({
  learners,
  showFacilitator,
}) {
  return (
    <ReportTable
      minimumWidth={
        showFacilitator
          ? "1560px"
          : "1320px"
      }
    >
      <thead className="bg-[#FFF9F5]">
        <tr>
          <TableHeading>
            Learner
          </TableHeading>

          {showFacilitator && (
            <TableHeading>
              Facilitator
            </TableHeading>
          )}

          <TableHeading>
            Current position
          </TableHeading>

          <TableHeading>
            Progress
          </TableHeading>

          <TableHeading>
            XP
          </TableHeading>

          <TableHeading>
            Latest level
          </TableHeading>

          <TableHeading>
            Activity
          </TableHeading>

          <TableHeading align="right">
            Action
          </TableHeading>
        </tr>
      </thead>

      <tbody className="divide-y divide-[#EFEFEF]">
        {learners.map(
          (learner) => {
            const progress =
              nullableNumber(
                learner.overall_progress_percent
              );

            return (
              <tr
                key={
                  learner.cohort_learner_id
                }
                className="align-top transition hover:bg-[#FFFDFB]"
              >
                <td className="px-5 py-4">
                  <p className="font-black">
                    {learner.full_name ||
                      "Learner"}
                  </p>

                  <p className="mt-1 text-xs font-semibold text-[#757575]">
                    {learner.email}
                  </p>
                </td>

                {showFacilitator && (
                  <td className="px-5 py-4">
                    <FacilitatorDetails
                      assignment={
                        learner.facilitator_assignment
                      }
                    />
                  </td>
                )}

                <td className="px-5 py-4 text-sm font-black">
                  {positionLabel(
                    learner
                  )}
                </td>

                <td className="px-5 py-4">
                  <div className="w-44">
                    <div className="flex justify-between gap-3 text-xs font-black">
                      <span>
                        {formatNumber(
                          learner.levels_completed
                        )}{" "}
                        of{" "}
                        {formatNumber(
                          learner.total_levels
                        )}
                      </span>

                      <span>
                        {formatPercentage(
                          progress
                        )}
                      </span>
                    </div>

                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EFEFEF]">
                      <div
                        className="h-full rounded-full bg-[#FF914D]"
                        style={{
                          width: `${Math.min(
                            Math.max(
                              progress ??
                                0,
                              0
                            ),
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4">
                  <p className="text-lg font-black">
                    {formatNumber(
                      learner.total_xp
                    )}
                  </p>

                  <p className="text-xs text-[#757575]">
                    Total XP
                  </p>
                </td>

                <td className="px-5 py-4">
                  <p className="max-w-[230px] text-sm font-black">
                    {learner.last_completed_level_title ||
                      "Not started"}
                  </p>

                  <p className="mt-1 text-xs text-[#757575]">
                    {formatDateTime(
                      learner.last_completed_at
                    )}
                  </p>
                </td>

                <td className="px-5 py-4">
                  <p className="text-sm font-black">
                    {activityDescription(
                      learner
                    )}
                  </p>

                  <p className="mt-1 text-xs text-[#757575]">
                    {formatDateTime(
                      learner.last_activity_at
                    )}
                  </p>
                </td>

                <td className="px-5 py-4 text-right">
                  <LearnerDetailsLink
                    learner={
                      learner
                    }
                  />
                </td>
              </tr>
            );
          }
        )}
      </tbody>
    </ReportTable>
  );
}

function TaskReport({
  learners,
  showFacilitator,
}) {
  return (
    <ReportTable
      minimumWidth={
        showFacilitator
          ? "1320px"
          : "1080px"
      }
    >
      <thead className="bg-[#FFF9F5]">
        <tr>
          <TableHeading>
            Learner
          </TableHeading>

          {showFacilitator && (
            <TableHeading>
              Facilitator
            </TableHeading>
          )}

          <TableHeading>
            Task status
          </TableHeading>

          <TableHeading>
            Completed
          </TableHeading>

          <TableHeading>
            Outstanding
          </TableHeading>

          <TableHeading>
            Completion rate
          </TableHeading>

          <TableHeading align="right">
            Action
          </TableHeading>
        </tr>
      </thead>

      <tbody className="divide-y divide-[#EFEFEF]">
        {learners.map(
          (learner) => {
            const status =
              taskStatus(
                learner
              );

            const total =
              nullableNumber(
                learner.tasks_total
              );

            const completed =
              nullableNumber(
                learner.tasks_completed
              );

            const completionRate =
              total !== null &&
              total > 0 &&
              completed !== null
                ? (completed /
                    total) *
                  100
                : null;

            return (
              <tr
                key={
                  learner.cohort_learner_id
                }
                className="align-top transition hover:bg-[#FFFDFB]"
              >
                <td className="px-5 py-4">
                  <p className="font-black">
                    {learner.full_name ||
                      "Learner"}
                  </p>

                  <p className="mt-1 text-xs font-semibold text-[#757575]">
                    {learner.email}
                  </p>
                </td>

                {showFacilitator && (
                  <td className="px-5 py-4">
                    <FacilitatorDetails
                      assignment={
                        learner.facilitator_assignment
                      }
                    />
                  </td>
                )}

                <td className="px-5 py-4">
                  <span
                    className={[
                      "inline-flex rounded-full px-3 py-1 text-xs font-black",
                      taskStatusClasses(
                        status
                      ),
                    ].join(
                      " "
                    )}
                  >
                    {status}
                  </span>
                </td>

                <td className="px-5 py-4">
                  <p className="text-lg font-black">
                    {formatNumber(
                      learner.tasks_completed
                    )}
                  </p>

                  <p className="text-xs text-[#757575]">
                    of{" "}
                    {formatNumber(
                      learner.tasks_total
                    )}
                  </p>
                </td>

                <td className="px-5 py-4 text-lg font-black">
                  {formatNumber(
                    learner.tasks_outstanding
                  )}
                </td>

                <td className="px-5 py-4 text-sm font-black">
                  {formatPercentage(
                    completionRate
                  )}
                </td>

                <td className="px-5 py-4 text-right">
                  <LearnerDetailsLink
                    learner={
                      learner
                    }
                  />
                </td>
              </tr>
            );
          }
        )}
      </tbody>
    </ReportTable>
  );
}

function FacilitatorDetails({
  assignment,
}) {
  if (
    !assignment
      ?.facilitator_partner_member_id
  ) {
    return (
      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-black text-gray-600">
        Unassigned
      </span>
    );
  }

  return (
    <div>
      <p className="text-sm font-black text-[#333333]">
        {assignment.facilitator_full_name ||
          "Facilitator"}
      </p>

      {assignment.facilitator_email && (
        <p className="mt-1 text-xs font-semibold text-[#757575]">
          {assignment.facilitator_email}
        </p>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-[22px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black">
            {value}
          </p>

          <p className="mt-1 text-sm text-[#757575]">
            {description}
          </p>
        </div>

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}

function ReportTable({
  children,
  minimumWidth,
}) {
  return (
    <section className="mt-6 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table
          className="w-full divide-y divide-[#EFEFEF]"
          style={{
            minWidth:
              minimumWidth,
          }}
        >
          {children}
        </table>
      </div>
    </section>
  );
}

function TableHeading({
  children,
  align = "left",
}) {
  return (
    <th
      scope="col"
      className={[
        "whitespace-nowrap px-5 py-3 text-xs font-black uppercase tracking-wider text-[#757575]",
        align === "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}

function MembershipBadge({
  status,
}) {
  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",
        membershipClasses(
          status
        ),
      ].join(" ")}
    >
      {status ||
        "Unknown"}
    </span>
  );
}

function LearnerDetailsLink({
  learner,
}) {
  return (
    <Link
      to={`/partner/learners/${learner.cohort_learner_id}`}
      className="inline-flex items-center rounded-full border border-[#FF914D] px-3 py-2 text-xs font-black text-[#FF914D] transition hover:bg-[#FFF4EC]"
    >
      <Eye className="mr-1.5 h-3.5 w-3.5" />

      View details
    </Link>
  );
}